import { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import cryptoRandomString from 'crypto-random-string';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
// Types
import QuizTypes from 'types/QuizTypes';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Async
import { getConsentDocuments } from 'store/consent/consentAsync';
// Selectors
import { selectQuiz } from 'store/quiz/quizSelectors';
import { selectAnalyticData, selectRegisterData, selectResultId } from 'store/auth/authSelectors';
import { selectConsentDocuments } from 'store/consent/consentSelectors';
// Actions
import { setPlayerData } from 'store/rokt/roktSlice';
import { setAnalyticData, setRegisterData } from 'store/auth/authSlice';
// Hooks
import useToggle from 'hooks/useToggle';
// Dialogs
import PostQuizQuestionDialog from 'dialogs/PostQuizQuestion.dialog';
import ConsentContentDialog from 'dialogs/ConsentContent.dialog';
// Components
import { Input, Checkbox } from 'components/Controls';
import { Button } from 'components/Buttons';
// Services
import StorageService from 'services/Storage.service';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { isNumeric } from 'utilities/Utilities';
import Phone from 'components/Phone';
import { isValidPhoneNumber } from 'react-phone-number-input';

// eslint-disable-next-line
const emailPattern = /(?!.*\.{2})^([a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(\.[a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+)*|"((([ \t]*\r\n)?[ \t]+)?([\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*(([ \t]*\r\n)?[ \t]+)?")@(([a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.)+([a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.?$/i;

interface IFormData {
  firstName: string;
  lastName: string;
  email: string;
  yearOfBirth: string;
  phoneNumber: string;
  consents: string[];
};

type Props = {
  onAsyncRegisterUser: (data:any, doRegister?:boolean, doRedirect?:boolean) => void;
  loading: boolean;
}

const PublicForm:FC<Props> = ({
  // Props
  onAsyncRegisterUser, loading
}) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  // State
  const quiz:any = useAppSelector(selectQuiz);
  const userRegisterData = useAppSelector(selectRegisterData);
  const userAnalyticData = useAppSelector(selectAnalyticData);
  const resultId = useAppSelector(selectResultId);
  const consentDocuments = useAppSelector(selectConsentDocuments);

  const [ selectedContent, setSelectedContent ] = useState<string>('');

  const { open:openPostQuizDialog, toggle:togglePostQuizDialog } = useToggle();
  const { open:openContentDialog, toggle:toggleContentDialog } = useToggle();

  const handleOpenContentDialog = (content: string | undefined) => {
    setSelectedContent(content || '');
    toggleContentDialog();
  }

  const [ qwid, setQwid ] = useState<string | null>(StorageService.getQwid());
  const [ isSubmitted, setIsSubmitted ] = useState(false);
  const [ formData, setFormData ] = useState<IFormData>({
    firstName: '',
    lastName: '',
    email: '',
    yearOfBirth: '',
    phoneNumber: '',
    consents: []
  });
  const [ errors, setErrors ] = useState({
    firstName: '',
    lastName: '',
    email: '',
    yearOfBirth: '',
    phoneNumber: ''
  });

  const navigateTo = useNavigateTo();

  useEffect(() => {
    const hasErrors = Object.values(errors).some(v => v);
    if ( isSubmitted && !hasErrors ){
      let registerData:any = null;
      let analyticData:any = {
        consents: formData.consents
      }

      if (!qwid) {
        const randomString = cryptoRandomString({ length: 11 });
        registerData = {
          name: formData.firstName,
          surname: formData.lastName,
          email: formData.email,
          password: randomString
        };
        if (formData.phoneNumber) registerData['phoneNumber'] = formData.phoneNumber;
        analyticData = {
          ...analyticData,
          yearOfBirth: formData.yearOfBirth,
        };
      }

      if (quiz.registrationPage.showPriorQuiz && !userRegisterData) {
        dispatch(setRegisterData(registerData || true));
        dispatch(setAnalyticData(analyticData));
        navigateTo('countdown');
        return;
      }

      if (quiz.registrationPage.showPriorQuiz && !!userRegisterData) {
        registerData = userRegisterData;
        analyticData = userAnalyticData;
      }

      if (!!qwid) {
        registerData = null;
      }
      
      if (
        [QuizTypes.Scoring, QuizTypes.Prediction].includes(quiz.type) &&
        (quiz.postQuizPages && quiz.postQuizPages[0] && quiz.postQuizPages[0].questions.length)
      ){
        onAsyncRegisterUser({ registerData, analyticData, togglePostQuizDialog }, false);
      } else {
        onAsyncRegisterUser({ registerData, analyticData });
      }
      if (!qwid) {
        dispatch(setPlayerData({
          firstname: formData.firstName,
          lastname: formData.lastName,
          email: formData.email,
          birthyear: formData.yearOfBirth,
          phoneNumber: formData.phoneNumber,
        }));
      }
    }
    // eslint-disable-next-line
  }, [isSubmitted, errors]);

  const handleChangeCheckbox = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    if ( name === 'checkAll' ){
      setFormData((prevState:any) => ({
        ...prevState,
        consents: consentDocuments?.map(document => document._id)
      }));
    } else {
      setFormData((prevState:any) => ({
        ...prevState,
        consents: checked ? [...prevState.consents, name] : prevState.consents.filter((c:string) => c !== name)
      }));
    }
  }

  const handleChangeInput = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState:any) => ({
      ...prevState,
      [name]: value
    }));
  }

  const handleChangePhone = (phone: string | undefined) => {
    setFormData((prevState:any) => ({
      ...prevState,
      phoneNumber: phone
    }));
  }

  const handleClick = () => {
    setIsSubmitted(true);
    if (qwid) return;
    setErrors((prevState:any) => ({
      ...prevState,
      firstName: !formData.firstName ? t('common.requiredField') : '',
      lastName: !formData.lastName ? t('common.requiredField') : '',
      email: !formData.email
        ? t('common.requiredField')
        : !emailPattern.test(formData.email)
          ? t('common.emailNotValid')
          : ''
      ,
      phoneNumber: validatePhoneNumber(formData.phoneNumber),
      yearOfBirth: validateYearOfBirth(formData.yearOfBirth)
    }));
  }

  const validatePhoneNumber = (phone: string | undefined) => {
    if (!quiz.registrationPage?.requirePhoneNumber) return '';
    if (!phone) return t('common.requiredField');
    if (!isValidPhoneNumber(phone)) return t('quiz.registerPage.phoneNotValid');
    return '';
  }

  const validateYearOfBirth = (yearOfBirth:string) => {
    if ( !yearOfBirth ) return '';
    if ( !isNumeric(yearOfBirth) ) return t('quiz.registerPage.fieldShouldBeNumeric');
    if ( yearOfBirth.length !== 4 ) return t('quiz.registerPage.yearNotCorrect');
    const currentYear = new Date().getFullYear();
    const yearToNum = Number(yearOfBirth);
    if ( yearToNum < 1920 || yearToNum > currentYear ) return t('quiz.registerPage.yearRangeNotCorrect');
    return '';
  }

  useEffect(() => {
    if (quiz.registrationPage.showPriorQuiz && !!userRegisterData && !!resultId) {
      setIsSubmitted(true);
    }
    // eslint-disable-next-line
  }, [resultId]);

  useEffect(() => {
    dispatch(getConsentDocuments(quiz.consents)).unwrap();
    // eslint-disable-next-line
  }, []);

  const requiredConsentsIds = consentDocuments?.filter(documnent => documnent.required).map(doc => doc._id) || [];
  
  return (
    <Fragment>
      {quiz.registrationPage.caption && !qwid && !userRegisterData ? (
        <h2 className="font-bold text-center text-lg mb-2">{quiz.registrationPage.caption}</h2>
      ) : null}
      {!!qwid && (
        <div className="mb-2 text-center">
          <h2 className="text-lg mb-2">{t('quiz.registerPage.alreadyRegistered')}</h2>
          <Button onClick={() => setQwid(null)}  className="mt-2 mb-4">
            {t('quiz.registerPage.registerNewUser')}
          </Button>
        </div>
      )}
      {[QuizTypes.Scoring, QuizTypes.Prediction].includes(quiz.type) && quiz.spotPrizesEnabled && quiz.spotPrizes && quiz.spotPrizes.length  && !qwid && !userRegisterData ? (
        <span className="block text-center text-blue-700 font-bold mb-4">{t('quiz.registerPage.youCouldWinRandomPrize')}</span>
      ) : null}
      {!userRegisterData && (
        <div>
          {!qwid && (
            <Fragment>
              <Input
                name="firstName" id="firstName" placeholder={t('common.firstName')}
                value={formData.firstName}
                onChange={handleChangeInput}
                error={Boolean(errors.firstName)}
                errorText={errors.firstName}
              />
              <Input
                name="lastName" id="lastName" placeholder={t('common.lastName')}
                value={formData.lastName}
                onChange={handleChangeInput}
                error={Boolean(errors.lastName)}
                errorText={errors.lastName}
              />
              <Input
                name="email" id="email" placeholder={t('common.email')}
                value={formData.email}
                onChange={handleChangeInput}
                error={Boolean(errors.email)}
                errorText={errors.email}
              />
              <Input
                name="yearOfBirth" id="yearOfBirth" placeholder={t('common.yearOfBirth')}
                value={formData.yearOfBirth}
                onChange={handleChangeInput}
                error={Boolean(errors.yearOfBirth)}
                errorText={errors.yearOfBirth}
              />
              {quiz.registrationPage?.requirePhoneNumber && (
                <Phone
                  name="phoneNumber"
                  placeholder={t('quiz.registerPage.phoneNumber')}
                  value={formData.phoneNumber}
                  onChange={handleChangePhone}
                  error={Boolean(errors.phoneNumber)}
                  errorText={errors.phoneNumber}
                />
              )}
            </Fragment>
          )}
          <div className="mb-4">
            <Button
              className="w-full hover:bg-none bg-primary hover:bg-primary text-white flex items-center justify-center gap-3"
              onClick={handleClick}
              disabled={!requiredConsentsIds.every(c => formData.consents.includes(c))}
              loading={loading}
            >
              {qwid ? t('common.proceed') : t('common.register')}
            </Button>
          </div>
          {!!consentDocuments?.length && (
            <Checkbox
              id="checkAll"
              name="checkAll"
              checked={formData.consents.length === consentDocuments?.length}
              onChange={handleChangeCheckbox}
            >
              <label
                htmlFor="checkAll"
                className="text-sm font-bold"
              >{t('quiz.registerPage.agreeAllTermsAndConditions')}</label>
            </Checkbox>
          )}
          <div className="pl-4 pt-4">
            {consentDocuments?.map(document => (
              <Checkbox
                key={document._id}
                className="mb-2"
                id={document._id}
                name={document._id}
                checked={formData.consents.includes(document._id)}
                onChange={handleChangeCheckbox}
              >
                <label
                  htmlFor={document._id}
                  className="text-xs"
                >
                  {document.required && (
                    <>
                      <span className="text-red-600">*</span>
                      &nbsp;
                    </>
                  )}
                  {document.shortText}
                  &nbsp;&nbsp;
                  {document.content ? (
                    <span className="font-bold cursor-pointer" onClick={(e) => {
                      e.preventDefault();
                      handleOpenContentDialog(document.content);
                    }}>
                      {t('quiz.registerPage.showMore')}
                    </span>
                  ) : (
                    <a className="font-bold cursor-pointer" href={document.url} target="_blank" rel="noreferrer">{t('quiz.registerPage.showMore')}</a>
                  )}
                  &nbsp;
                </label>
              </Checkbox>
            ))}
          </div>
        </div>
      )}
      {openPostQuizDialog ? (
        <PostQuizQuestionDialog
          onClose={togglePostQuizDialog}
          onAsyncRegisterUser={onAsyncRegisterUser}
          loading={loading}
        />
      ) : null}
      {openContentDialog ? (
        <ConsentContentDialog onClose={toggleContentDialog} content={selectedContent} />
      ) : null}
    </Fragment>
  )
}

export default PublicForm;
